import { type PolicyStatus } from "../policiesData"

export type PolicyItemAppearance = {
  fillColor: string
  borderColor: string
  borderStyle: "solid" | "dashed"
  iconColor: string
}

const POLICY_BOX_DARK = "#1a1a1a"

// Visual ramp: solid ("current" / "in_discussion") → dashed ("off_the_table").
export const POLICY_ITEM_APPEARANCE: Record<PolicyStatus, PolicyItemAppearance> = {
  current: {
    fillColor: "black",
    borderColor: POLICY_BOX_DARK,
    borderStyle: "solid",
    iconColor: "white",
  },
  in_discussion: {
    fillColor: "black",
    borderColor: "rgba(26, 26, 26, 0.33)",
    borderStyle: "solid",
    iconColor: "white",
  },
  off_the_table: {
    fillColor: "black",
    borderColor: "rgba(26, 26, 26, 0.5)",
    borderStyle: "dashed",
    iconColor: "white",
  },
}

// The production HUD's per-status policy box treatment (from HUD 87):
// "current" and "off the table" keep the plain black-outlined look, while
// "in discussion" boxes fill dark red (matching the graph's past-capability
// line) with no outline and white icons. Also drives the mini square beside
// the policy name in the in-canvas hover detail (policyHoverSwap.tsx), so the
// two always match.
export const HUD87_POLICY_STATUS_APPEARANCE: Record<PolicyStatus, PolicyItemAppearance> = {
  current: {
    fillColor: "transparent",
    borderColor: "#000",
    borderStyle: "solid",
    iconColor: "#000",
  },
  in_discussion: {
    // Border matches the fill so the box reads as outline-less while keeping
    // the same 1.5px footprint as the outlined boxes (motion also can't
    // animate borderColor to the "transparent" keyword).
    fillColor: "#8B0000",
    borderColor: "#8B0000",
    borderStyle: "solid",
    iconColor: "#fff",
  },
  off_the_table: {
    fillColor: "transparent",
    borderColor: "#000",
    borderStyle: "solid",
    iconColor: "#000",
  },
}

// ─── Hovered-square highlight treatments (tabs 23–27) ───────────────────────
// How the policy square under the pointer marks itself while its detail shows
// in the below-HUD map — set per-variant via PolicyHoverSwap's squareHighlight
// prop; PolicyItem reads it from the swap context directly. Each treatment
// reserves no extra layout (overlays, transforms, inward borders only), and
// every one also turns the hovered status LABEL red
// (POLICY_LABEL_HOVER_COLOR, applied in PoliciesSection). Colors are literal
// hexes, not var(--accent): motion animates them, and it can't interpolate a
// CSS var. #8B0000 is this canvas's standard red (--race-background, the
// in-discussion squares' own fill).
export type PolicySquareHighlight =
  | "red-fill-invert"
  | "halo"
  | "pop"
  | "red-bar"
  | "inset-ring"

// The hovered status label's text color, on every square-highlight tab.
export const POLICY_LABEL_HOVER_COLOR = "#8B0000"

// Tab 23 "red-fill-invert" — the outlined squares (current / off the table)
// fill solid red with a white icon; the already-red in-discussion squares
// INVERT: page-background fill, red outline, red icon.
export const POLICY_SQUARE_RED_FILL_INVERT_STYLE = {
  outlined: { fillColor: "#8B0000", borderColor: "#8B0000", iconColor: "#fff" },
  filled: { fillColor: "#fffff8", borderColor: "#8B0000", iconColor: "#8B0000" },
}

// Tab 24 "halo" — a red ring floats just off the square's edge (a sliver of
// page background between them), leaving the square itself untouched. Painted
// as box-shadows, so nothing reflows; it may brush the neighbouring squares.
export const POLICY_SQUARE_HALO_STYLE = {
  gapWidthPx: 1,
  gapColor: "#fffff8",
  ringWidthPx: 1.5,
  ringColor: "#8B0000",
}

// Tab 25 "pop" — the hovered square scales up over its neighbours (transform
// only, no reflow); colors stay exactly as they are.
export const POLICY_SQUARE_POP_STYLE = {
  scale: 1.45,
}

// Tab 26 "red-bar" — a short red bar appears just under the hovered square
// (the breakdown's underline vocabulary at square scale).
export const POLICY_SQUARE_RED_BAR_STYLE = {
  barHeightPx: 2,
  barGapPx: 2,
  barColor: "#8B0000",
  barRadiusPx: 1,
}

// Tab 27 "inset-ring" — the square's border thickens into a ring: red on the
// outlined squares, page-background (a cream inset) on the red-filled ones.
// Borders grow inward (border-box), so the grid never moves.
export const POLICY_SQUARE_INSET_RING_STYLE = {
  ringWidthPx: 3,
  outlinedRingColor: "#8B0000",
  filledRingColor: "#fffff8",
}

// Panel-level appearance override props (PoliciesPanel's strokeColor /
// iconColor / strokeStyle / backgroundColor knobs), applied uniformly across
// all three statuses.
export type PolicyAppearanceOverrides = {
  strokeColor?: string
  iconColor?: string
  strokeStyle?: "solid" | "dashed"
  backgroundColor?: string
}

// Applies the panel-level overrides on top of a base per-status appearance
// map, producing the appearances the policy boxes actually render with —
// shared by the panel columns and the tooltip's row boxes so they can't
// drift apart. iconColor falls back to strokeColor (preserving the panel's
// prior behavior).
export const resolvePolicyStatusAppearances = (
  base: Record<PolicyStatus, PolicyItemAppearance>,
  { strokeColor, iconColor, strokeStyle, backgroundColor }: PolicyAppearanceOverrides,
): Record<PolicyStatus, PolicyItemAppearance> => {
  const resolvedIconColor = iconColor ?? strokeColor
  const overrides = {
    ...(strokeColor !== undefined ? { borderColor: strokeColor } : {}),
    ...(resolvedIconColor !== undefined ? { iconColor: resolvedIconColor } : {}),
    ...(strokeStyle !== undefined ? { borderStyle: strokeStyle } : {}),
    ...(backgroundColor !== undefined ? { fillColor: backgroundColor } : {}),
  }
  return {
    current: { ...base.current, ...overrides },
    in_discussion: { ...base.in_discussion, ...overrides },
    off_the_table: { ...base.off_the_table, ...overrides },
  }
}

// "Filled vs. outline" treatment used by the fill/outline lower-HUD variant:
// live policies ("current" / "in_discussion") read as solid black boxes with
// white icons, while "off_the_table" policies drop to a black outline only —
// no fill, black icon. Pass this to <PoliciesPanel statusAppearance={...} />.
export const POLICY_FILL_VS_OUTLINE_APPEARANCE: Record<PolicyStatus, PolicyItemAppearance> = {
  current: {
    fillColor: "#000",
    borderColor: "#000",
    borderStyle: "solid",
    iconColor: "#fff",
  },
  in_discussion: {
    fillColor: "#000",
    borderColor: "#000",
    borderStyle: "solid",
    iconColor: "#fff",
  },
  off_the_table: {
    fillColor: "transparent",
    borderColor: "#000",
    borderStyle: "solid",
    iconColor: "#000",
  },
}
